import { ref, Ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { Product } from './useProductDetail';
import api from '@/services/api';

interface Toast {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

export function useProductFollow(product: Ref<Product | null>) {
  const router = useRouter();
  const following = ref(false);
  const followersCount = ref(0);
  const toast = ref<Toast>({
    show: false,
    message: '',
    type: 'info',
  });
  const isLoading = ref(false);

  // Initialize followers count when product loads
  watch(
    () => product.value,
    (newProduct) => {
      if (newProduct) {
        followersCount.value = newProduct.followersCount || 0;
        // Check if user is following this product
        checkFollowingStatus(newProduct.id);
      }
    },
    { immediate: true }
  );

  // Check if the user is following this product
  const checkFollowingStatus = async (productId: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      // User is not logged in, so they're not following
      following.value = false;
      return;
    }

    try {
      const response = await api.get(
        `/api/product-follows/products/${productId}/is-following`
      );

      if (response.data.success) {
        following.value = response.data.data.isFollowing;
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
      // In case of error, assume not following
      following.value = false;
    }
  };

  const handleContactSeller = () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      // Save detailed information before redirecting to login
      localStorage.setItem(
        'redirectAfterLogin',
        router.currentRoute.value.fullPath
      );

      // Also save chat-specific info in a separate key
      localStorage.setItem(
        'chatRedirectInfo',
        JSON.stringify({
          otherUserId: product.value?.user.id,
          productId: product.value?.id,
          productName: product.value?.name,
          productSlug: product.value?.slug,
          otherUsername: product.value?.user.username, // Add username
        })
      );

      showLoginRequiredToast(
        'Morate biti prijavljeni da biste kontaktirali prodavca.'
      );
      return;
    }

    // User is logged in, redirect to chat with seller
    if (product.value && product.value.user) {
      router.push({
        name: 'messages',
        query: {
          otherUserId: product.value.user.id,
          productId: product.value.id,
          productName: product.value.name,
          productSlug: product.value.slug,
          otherUsername: product.value.user.username, // Include username in the query
        },
      });
    }
  };

  const handleToggleFollow = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      showLoginRequiredToast('Morate biti prijavljeni da biste pratili oglas.');
      return;
    }

    // Prevent multiple clicks
    if (isLoading.value) return;

    // User is logged in, proceed with toggle follow
    isLoading.value = true;

    try {
      if (!product.value) return;

      if (following.value) {
        // Unfollow product
        await unfollowProduct(product.value.id);
      } else {
        // Follow product
        await followProduct(product.value.id);
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);

      toast.value = {
        show: true,
        message: 'Došlo je do greške. Pokušajte ponovo.',
        type: 'error',
      };
    } finally {
      isLoading.value = false;
    }
  };

  const followProduct = async (productId: string) => {
    const response = await api.post(
      `/api/product-follows/products/${productId}/follow`
    );

    if (response.data.success) {
      following.value = true;
      followersCount.value += 1;

      toast.value = {
        show: true,
        message: 'Uspešno ste počeli da pratite oglas!',
        type: 'success',
      };
    } else {
      throw new Error('Failed to follow product');
    }
  };

  const unfollowProduct = async (productId: string) => {
    const response = await api.delete(
      `/api/product-follows/products/${productId}/unfollow`
    );

    if (response.data.success) {
      following.value = false;
      followersCount.value = Math.max(0, followersCount.value - 1);

      toast.value = {
        show: true,
        message: 'Prestali ste da pratite oglas.',
        type: 'info',
      };
    } else {
      throw new Error('Failed to unfollow product');
    }
  };

  const showLoginRequiredToast = (message: string) => {
    // Show toast notification
    toast.value = {
      show: true,
      message,
      type: 'info',
    };

    // Save current path to localStorage for redirect after login
    localStorage.setItem(
      'redirectAfterLogin',
      router.currentRoute.value.fullPath
    );

    // Redirect to login page after a short delay to see the notification
    setTimeout(() => {
      router.push('/login');
    }, 1500);
  };

  return {
    following,
    followersCount,
    toast,
    isLoading,
    handleContactSeller,
    handleToggleFollow,
  };
}
